import type { SearchHit } from '../search/contentSearch';
import { searchContent } from '../search/contentSearch';
import type { Bm25Index } from '../search/bm25';
import type { HitMeta } from '../search/contentSearch';
import { escapeHtml } from '../utils/escapeHtml';

export type SidebarSearchOpts = {
  ensureIndex: () => Promise<Bm25Index<HitMeta>>;
  onSelectHit: (hit: SearchHit) => void | Promise<void>;
};

export class SidebarSearch {
  private readonly ensureIndex: () => Promise<Bm25Index<HitMeta>>;
  private readonly onSelectHit: (hit: SearchHit) => void | Promise<void>;
  private root: HTMLElement | null = null;
  private input: HTMLInputElement | null = null;
  private resultsEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: SidebarSearchOpts) {
    this.ensureIndex = opts.ensureIndex;
    this.onSelectHit = opts.onSelectHit;
  }

  mount(sidebarInner: HTMLElement): void {
    const host =
      sidebarInner.querySelector<HTMLElement>('#sidebar-search-host') ??
      sidebarInner.querySelector<HTMLElement>('#sidebar-controls');
    const searchWrap = document.createElement('div');
    searchWrap.className = 'sidebar-search';
    searchWrap.innerHTML = `
      <input type="search" class="sidebar-search-input" placeholder="Search..." autocomplete="off" spellcheck="false" aria-label="Search" />
      <p class="sidebar-search-status" aria-live="polite"></p>
      <ul class="sidebar-search-results hidden" role="listbox" aria-label="Search results"></ul>
    `;
    if (host) host.appendChild(searchWrap);
    else sidebarInner.insertBefore(searchWrap, sidebarInner.querySelector('.sidebar-nav'));

    this.root = searchWrap;
    this.input = searchWrap.querySelector('.sidebar-search-input');
    this.resultsEl = searchWrap.querySelector('.sidebar-search-results');
    this.statusEl = searchWrap.querySelector('.sidebar-search-status');

    this.input!.addEventListener('input', () => this.scheduleSearch());
    this.input!.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        this.clear();
        this.input!.blur();
      }
      if (e.key === 'Enter') {
        const first = this.resultsEl!.querySelector<HTMLButtonElement>('.sidebar-search-hit');
        first?.click();
      }
    });

    document.addEventListener('keydown', e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        this.input!.focus();
        this.input!.select();
      }
    });
  }

  focus(): void {
    this.input?.focus();
    this.input?.select();
  }

  clear(): void {
    if (this.input) this.input.value = '';
    this.resultsEl?.classList.add('hidden');
    if (this.statusEl) this.statusEl.textContent = '';
    this.resultsEl?.replaceChildren();
  }

  private scheduleSearch(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => void this.runSearch(), 180);
  }

  private async runSearch(): Promise<void> {
    const q = this.input?.value.trim() ?? '';
    if (!this.resultsEl || !this.statusEl) return;

    if (q.length < 2) {
      this.resultsEl.classList.add('hidden');
      this.statusEl.textContent = q.length === 0 ? '' : 'Type at least 2 characters';
      this.resultsEl.replaceChildren();
      return;
    }

    this.statusEl.textContent = 'Searching…';
    this.resultsEl.classList.remove('hidden');

    try {
      this.statusEl.textContent = 'Searching…';
      const index = await this.ensureIndex();
      const hits = searchContent(index, q, 30);

      if (hits.length === 0) {
        this.resultsEl.replaceChildren();
        const li = document.createElement('li');
        li.className = 'sidebar-search-empty';
        li.textContent = 'No matches';
        this.resultsEl.appendChild(li);
        this.statusEl.textContent = 'No matches';
        return;
      }

      this.statusEl.textContent = `${hits.length} result${hits.length === 1 ? '' : 's'}`;
      this.resultsEl.replaceChildren();
      for (const hit of hits) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sidebar-search-hit';
        btn.setAttribute('role', 'option');
        const kindLabel =
          hit.kind === 'page' ? 'Page' : hit.kind === 'section' ? 'Section' : 'Item';
        btn.innerHTML = `
          <span class="sidebar-search-hit-title">${escapeHtml(hit.title)}</span>
          <span class="sidebar-search-hit-meta">${escapeHtml(kindLabel)} · ${escapeHtml(hit.pageName)}</span>
          <span class="sidebar-search-hit-snippet">${escapeHtml(hit.snippet)}</span>
        `;
        btn.addEventListener('click', () => {
          void this.onSelectHit(hit);
          this.clear();
        });
        li.appendChild(btn);
        this.resultsEl.appendChild(li);
      }
    } catch (err) {
      this.statusEl.textContent = 'Search failed';
      this.resultsEl.replaceChildren();
      const li = document.createElement('li');
      li.className = 'sidebar-search-empty';
      li.textContent = err instanceof Error ? err.message : 'Error';
      this.resultsEl.appendChild(li);
    }
  }
}
